import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiClock, FiTrendingUp, FiFileText, FiEdit3, FiAlertTriangle, FiCheckCircle, FiCalendar, FiBook, FiChevronRight } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/api';
import quizApiService from '../../services/quizApi';
import notesApiService from '../../services/notesApi';
import { formatUTCToChicago, getWeekEndDate, isSubmissionLate, nowChicago, utcToChicago, getCurrentWeekNumber } from '../../utils/datetime-comprehensive';
import { DateTime } from 'luxon';
import InstructorDashboard from './InstructorDashboard';
import AdminDashboard from './AdminDashboard';
import ActiveAttendanceSessions from './ActiveAttendanceSessions';
import logger from '../../utils/logger';
import styles from './Dashboard.module.css';

// Helper function to get status icon class
const getStatusIconClass = (status) => {
  switch (status) {
    case 'pending': return styles.summaryItemIcon + ' ' + styles.pending;
    case 'overdue': return styles.summaryItemIcon + ' ' + styles.overdue;
    case 'completed': return styles.summaryItemIcon + ' ' + styles.completed;
    case 'in-progress': return styles.summaryItemIcon + ' ' + styles.inProgress;
    default: return styles.summaryItemIcon + ' ' + styles.pending;
  }
};

// Helper function to get stat icon class
const getStatIconClass = (color) => {
  switch (color) {
    case 'primary': return styles.statIcon + ' ' + styles.primary;
    case 'success': return styles.statIcon + ' ' + styles.success;
    case 'warning': return styles.statIcon + ' ' + styles.warning;
    default: return styles.statIcon + ' ' + styles.primary;
  }
};

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeAssignments: 0,
    completedAssignments: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  // showBackToTop removed - using GlobalScrollToTop from App.js
  const [assignments, setAssignments] = useState([]);
  const [currentWeeklyAssignments, setCurrentWeeklyAssignments] = useState([]);
  const [allActiveAssignments, setAllActiveAssignments] = useState([]); // Store all active assignments for summary calculation
  const [quizzes, setQuizzes] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showPreviousCourses, setShowPreviousCourses] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    // Don't fetch data if user is null
    if (!user) {
      return;
    }
    
    try {
      // Only make student-specific API calls for students
      const apiCalls = [
        apiService.getDashboardStats(),
        apiService.getRecentActivity(),
        apiService.getCourses(),
        apiService.getAssignments()
      ];

      // Only make student-specific API calls for students
      if (user.role === 'student') {
        apiCalls.push(
          quizApiService.getStudentAssignments(),
          notesApiService.getMyNotes({ pageSize: 10 })
        );
      }

      // CRITICAL FIX: Use Promise.allSettled instead of Promise.all to handle partial failures gracefully
      // This prevents one failed request from causing all responses to be undefined
      const responses = await Promise.allSettled(apiCalls);
      
      // Extract results, handling both fulfilled and rejected promises
      const statsResponse = responses[0]?.status === 'fulfilled' ? responses[0].value : null;
      const activityResponse = responses[1]?.status === 'fulfilled' ? responses[1].value : null;
      const coursesResponse = responses[2]?.status === 'fulfilled' ? responses[2].value : null;
      const assignmentsResponse = responses[3]?.status === 'fulfilled' ? responses[3].value : null;
      const quizzesResponse = user.role === 'student' && responses[4]?.status === 'fulfilled' ? responses[4].value : null;
      const notesResponse = user.role === 'student' && responses[5]?.status === 'fulfilled' ? responses[5].value : null;

      // CRITICAL FIX: Check if user still exists (might have logged out during fetch)
      if (!user) {
        return; // User logged out, don't process responses
      }

      if (statsResponse?.success && statsResponse.stats) {
        setStats(statsResponse.stats);
      }

      if (activityResponse?.success && activityResponse.activities) {
        setRecentActivities(activityResponse.activities);
      }

      if (coursesResponse?.success && coursesResponse.courses) {
        // Filter only enrolled courses
        const enrolled = coursesResponse.courses.filter(course => course.is_enrolled);
        setEnrolledCourses(enrolled);
      }

      if (assignmentsResponse?.success && assignmentsResponse.assignments) {
        // Filter to only show assignments from active courses
        const activeAssignments = assignmentsResponse.assignments.filter(
          assignment => assignment.course_is_active === 1 || assignment.course_is_active === true
        );
        // Store all active assignments for summary calculation
        setAllActiveAssignments(activeAssignments);

        // Separate weekly assignments that are due this week
        const weeklyDueThisWeek = [];
        const otherAssignments = [];

        activeAssignments.forEach(assignment => {
          if (assignment.allow_weekly_submissions) {
            const startOfCurrentCalendarWeek = nowChicago().startOf('week');
            const endOfCurrentCalendarWeek = nowChicago().endOf('week');
            const now = nowChicago();
            const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];

            const overdueWeeks = [];
            let nextOpenWeek = null;
            let nextOpenWeekDueDate = null;

            // Respect assignment max weekly submissions (fallback to 1)
            const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
            const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
              ? Math.floor(parsedMaxWeeks)
              : 1;
            
            // Iterate through possible weeks (e.g., up to 52 for a year-long assignment)
            for (let weekNumber = 1; weekNumber <= maxWeeks; weekNumber++) {
              let weekDueDate;
              if (weekNumber === 1 && assignment.due_date) {
                weekDueDate = utcToChicago(assignment.due_date); // Use assignment due_date for week 1
              } else if (assignment.start_date) {
                weekDueDate = getWeekEndDate(assignment.start_date, weekNumber, breakPeriods);
              } else {
                continue; // No start date, cannot calculate weekly due dates
              }

              if (!weekDueDate) continue;

              // Check if a submission exists for this specific week
              // Use submitted_week_numbers array if available (from backend), otherwise fallback to checking assignment.week_number
              let isSubmittedForThisWeek = false;
              if (assignment.submitted_week_numbers) {
                // Handle both string (JSON) and array formats
                let submittedWeeks = assignment.submitted_week_numbers;
                if (typeof submittedWeeks === 'string') {
                  try {
                    submittedWeeks = JSON.parse(submittedWeeks);
                  } catch (e) {
                    // If parsing fails, treat as empty array
                    submittedWeeks = [];
                  }
                }
                if (Array.isArray(submittedWeeks)) {
                  // Backend provides array of submitted week numbers
                  // Handle both number and string formats (JSON_ARRAYAGG might return strings)
                  isSubmittedForThisWeek = submittedWeeks.some(w => Number(w) === weekNumber);
                }
              }
              
              // Fallback: if submitted_week_numbers not available, check assignment.week_number
              if (!isSubmittedForThisWeek && assignment.submitted_at && assignment.week_number) {
                // Only mark as submitted if this is the exact week_number returned
                isSubmittedForThisWeek = assignment.week_number === weekNumber;
              }

              // Skip if already submitted for this week
              if (isSubmittedForThisWeek) continue;

              // Categorize weeks as overdue or pending
              if (weekDueDate < now) {
                // Overdue: due date is in the past
                overdueWeeks.push({
                  week_number: weekNumber,
                  display_due_date: weekDueDate
                });
              } else {
                // Pending/Open: due date is in the future - only track the NEXT upcoming one
                if (!nextOpenWeek && weekDueDate > now) {
                  nextOpenWeek = weekNumber;
                  nextOpenWeekDueDate = weekDueDate;
                  // Found the next open week, break since we only want the current open one
                  break;
                }
              }

              // If weekDueDate is too far in the future (more than 2 months), break the loop
              if (weekDueDate > now.plus({ weeks: 8 })) {
                break;
              }
            }

            // Helper to create week-specific assignment object
            // Clears submitted_at and status if this specific week hasn't been submitted
            const createWeekAssignment = (weekNum, dueDate) => {
              // Check if THIS specific week was submitted
              let isThisWeekSubmitted = false;
              if (assignment.submitted_week_numbers) {
                let submittedWeeks = assignment.submitted_week_numbers;
                if (typeof submittedWeeks === 'string') {
                  try {
                    submittedWeeks = JSON.parse(submittedWeeks);
                  } catch (e) {
                    submittedWeeks = [];
                  }
                }
                if (Array.isArray(submittedWeeks)) {
                  isThisWeekSubmitted = submittedWeeks.some(w => Number(w) === weekNum);
                }
              }
              
              const weekAssignment = {
                ...assignment,
                week_number: weekNum,
                display_due_date: dueDate,
              };
              
              // Clear submitted_at and status if THIS week wasn't submitted
              if (!isThisWeekSubmitted) {
                weekAssignment.submitted_at = null;
                weekAssignment.status = null;
              }
              
              return weekAssignment;
            };

            // Add overdue weeks to weeklyDueThisWeek (if they fall in current calendar week) or otherAssignments
            overdueWeeks.forEach(week => {
              const weekAssignment = createWeekAssignment(week.week_number, week.display_due_date);
              if (week.display_due_date >= startOfCurrentCalendarWeek && week.display_due_date <= endOfCurrentCalendarWeek) {
                weeklyDueThisWeek.push(weekAssignment);
              } else {
                otherAssignments.push(weekAssignment);
              }
            });

            // Add only the NEXT open week if it has actually started (not just future due date)
            // A week has started if its start date (start_date + (weekNumber-1) weeks) is <= now
            if (nextOpenWeek && nextOpenWeekDueDate) {
              // Calculate when this week actually starts
              let weekStartDate = null;
              if (assignment.start_date) {
                if (nextOpenWeek === 1) {
                  weekStartDate = utcToChicago(assignment.start_date);
                } else {
                  // Week N starts at start_date + (N-1) weeks
                  const startDate = utcToChicago(assignment.start_date);
                  weekStartDate = startDate.plus({ weeks: nextOpenWeek - 1 });
                }
              }
              
              // Only show the week if it has actually started
              if (weekStartDate && weekStartDate.isValid && weekStartDate <= now) {
                const weekAssignment = createWeekAssignment(nextOpenWeek, nextOpenWeekDueDate);
                if (nextOpenWeekDueDate >= startOfCurrentCalendarWeek && nextOpenWeekDueDate <= endOfCurrentCalendarWeek) {
                  weeklyDueThisWeek.push(weekAssignment);
                } else {
                  otherAssignments.push(weekAssignment);
                }
              }
            }

          } else {
            otherAssignments.push(assignment);
          }
        });
        setCurrentWeeklyAssignments(weeklyDueThisWeek);
        setAssignments(otherAssignments);
      }

      // Only process student-specific responses for students
      if (user.role === 'student') {
        if (quizzesResponse?.success && quizzesResponse.data) {
          setQuizzes(quizzesResponse.data);
        }

        if (notesResponse?.success && notesResponse.items) {
          setNotes(notesResponse.items);
        }
      }
    } catch (error) {
      // CRITICAL FIX: Don't log errors if user has logged out (401 is expected)
      // Check if error is due to logout/authentication
      const isAuthError = error?.status === 401 || 
                         error?.message?.includes('Token revoked') ||
                         error?.message?.includes('session has expired') ||
                         error?.message?.includes('logged out');
      
      // Only log non-auth errors (auth errors during logout are expected)
      if (!isAuthError) {
        logger.error('Error fetching dashboard data', { 
          error, 
          errorMessage: error?.message, 
          errorStack: error?.stack,
          errorName: error?.name 
        });
        console.error('Dashboard error details:', error);
      }
      
      // Only update state if user still exists (don't update if they logged out)
      if (user) {
        setStats({
          totalCourses: 0,
          activeAssignments: 0,
          completedAssignments: 0
        });
        setRecentActivities([]);
        setEnrolledCourses([]);
        setAssignments([]);
        setQuizzes([]);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    } else {
      // Clear all state when user is null
      setStats(null);
      setRecentActivities([]);
      setEnrolledCourses([]);
      setAssignments([]);
      setQuizzes([]);
    }
  }, [user, fetchDashboardData]);

  // Ensure page starts at top on mobile when component mounts
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Scroll handlers removed - using GlobalScrollToTop from App.js

  const getAssignmentStatus = useCallback((assignment) => {
    // If it's a weekly assignment with a specific week_number and display_due_date
      if (assignment.allow_weekly_submissions && assignment.week_number && assignment.start_date) {
      const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
      const now = nowChicago();
      // Use display_due_date if available (already calculated), otherwise calculate it
      const weekDueDate = assignment.display_due_date || getWeekEndDate(assignment.start_date, assignment.week_number, breakPeriods);

      // Check if THIS specific week has been submitted
      let isThisWeekSubmitted = false;
      if (assignment.submitted_week_numbers) {
        let submittedWeeks = assignment.submitted_week_numbers;
        if (typeof submittedWeeks === 'string') {
          try {
            submittedWeeks = JSON.parse(submittedWeeks);
          } catch (e) {
            submittedWeeks = [];
          }
        }
        if (Array.isArray(submittedWeeks)) {
          isThisWeekSubmitted = submittedWeeks.some(w => Number(w) === assignment.week_number);
        }
      }
      // Fallback to submitted_at if submitted_week_numbers not available
      if (!isThisWeekSubmitted && assignment.submitted_at) {
        isThisWeekSubmitted = true;
      }

      if (isThisWeekSubmitted && assignment.submitted_at) {
        // If submitted, check if it was late for THIS week
        if (isSubmissionLate(assignment.start_date, assignment.week_number, assignment.submitted_at, assignment.due_date, breakPeriods)) {
          return 'late';
        }
        return 'completed';
      } else {
        // If not submitted, check if the current week's due date is overdue
        if (weekDueDate && weekDueDate < now) {
          return 'overdue';
        }
        return 'pending';
      }
    }

    // Existing logic for non-weekly assignments and general assignments (not current week's weekly)
    if (assignment.submitted_at) {
      if (assignment.due_date) {
        const submittedAt = new Date(assignment.submitted_at);
        const dueDate = new Date(assignment.due_date);
        if (!isNaN(dueDate.getTime()) && dueDate.getTime() !== 0 && submittedAt > dueDate) {
          return 'late';
        }
      }
      return 'completed';
    }

    if (!assignment.due_date || assignment.due_date === null || assignment.due_date === undefined) {
      return 'pending';
    }
    
    const now = nowChicago();
    let dueDate;
    try {
      dueDate = utcToChicago(assignment.due_date);
    } catch (e) {
      return 'pending';
    }

    if (!dueDate || !dueDate.isValid) {
      return 'pending';
    }

    return dueDate < now ? 'overdue' : 'pending';
  }, []);

  const getQuizStatus = useCallback((quiz) => {
    switch (quiz.status) {
      case 'completed': return 'completed';
      case 'in_progress': return 'in-progress';
      default: {
        // Check if quiz is overdue
        if ((quiz.status === 'not_started' || quiz.status === 'in_progress') &&
            (quiz.assignment_due_at || quiz.quiz_due_at)) {
          const dueDate = new Date(quiz.assignment_due_at || quiz.quiz_due_at);
          const now = new Date();
          if (!isNaN(dueDate.getTime()) && dueDate.getTime() !== 0 && dueDate < now) {
            return 'overdue';
          }
        }
        return 'pending';
      }
    }
  }, []);

  const formatDueDate = (dueDate, assignment = null) => {
    if (assignment?.allow_weekly_submissions && assignment?.week_number && assignment?.display_due_date) {
      return formatUTCToChicago(assignment.display_due_date.toUTC().toISO(), 'M/d/yyyy \'at\' h:mm a');
    }
    
    if (!dueDate || dueDate === null || dueDate === undefined) {
      return 'No Due Date';
    }
    
    // Check if it's a valid date and not the Unix epoch
    const date = new Date(dueDate);
    if (isNaN(date.getTime()) || date.getTime() === 0) {
      return 'No Due Date';
    }
    
    return formatUTCToChicago(dueDate, 'M/d/yyyy \'at\' h:mm a');
  };

  // Memoized computed values for performance (must be called before any early returns)
  const assignmentSummary = useMemo(() => {
    try {
      // For display counts, use the filtered assignments (overdue/open)
      const allAssignmentsForDisplay = [...currentWeeklyAssignments, ...assignments];
      const overdue = allAssignmentsForDisplay.filter(assignment => {
        try {
          return getAssignmentStatus(assignment) === 'overdue';
        } catch (e) {
          console.error('Error getting assignment status for overdue:', e, assignment);
          return false;
        }
      }).length;
      const pending = allAssignmentsForDisplay.filter(assignment => {
        try {
          return getAssignmentStatus(assignment) === 'pending';
        } catch (e) {
          console.error('Error getting assignment status for pending:', e, assignment);
          return false;
        }
      }).length;
      const dueSoon = pending; // In the current setup, 'pending' effectively means 'due soon' if not overdue
      
      // For completed count, count ALL completed assignments from all active courses
      // This includes completed non-weekly assignments and all completed weeks of weekly assignments
      let completed = 0;
      
      if (Array.isArray(allActiveAssignments)) {
        allActiveAssignments.forEach(assignment => {
          if (!assignment) return; // Skip null/undefined assignments
          try {
            if (assignment.allow_weekly_submissions) {
              // For weekly assignments, count all completed weeks
              if (assignment.submitted_week_numbers) {
                let submittedWeeks = assignment.submitted_week_numbers;
                if (typeof submittedWeeks === 'string') {
                  try {
                    submittedWeeks = JSON.parse(submittedWeeks);
                  } catch (e) {
                    submittedWeeks = [];
                  }
                }
                if (Array.isArray(submittedWeeks)) {
                  // Count each completed week
                  completed += submittedWeeks.length;
                }
              }
            } else {
              // For non-weekly assignments, count if submitted
              if (assignment.submitted_at) {
                completed += 1;
              }
            }
          } catch (e) {
            console.error('Error processing assignment for completed count:', e, assignment);
          }
        });
      }
      
      return { overdue, dueSoon, completed };
    } catch (error) {
      console.error('Error calculating assignment summary:', error);
      return { overdue: 0, dueSoon: 0, completed: 0 };
    }
  }, [assignments, currentWeeklyAssignments, allActiveAssignments, getAssignmentStatus]);

  const recentAssignments = useMemo(() => {
    // Combine current weekly assignments with other assignments
    const allRelevantAssignments = [...currentWeeklyAssignments, ...assignments];

    // Filter to only unsubmitted assignments (or submitted for current weekly if it's overdue)
    const unsubmitted = allRelevantAssignments.filter(assignment => {
      if (assignment.allow_weekly_submissions && assignment.week_number) {
        // For weekly assignments, check if THIS specific week has been submitted
        let isThisWeekSubmitted = false;
        if (assignment.submitted_week_numbers) {
          let submittedWeeks = assignment.submitted_week_numbers;
          if (typeof submittedWeeks === 'string') {
            try {
              submittedWeeks = JSON.parse(submittedWeeks);
            } catch (e) {
              submittedWeeks = [];
            }
          }
          if (Array.isArray(submittedWeeks)) {
            isThisWeekSubmitted = submittedWeeks.some(w => Number(w) === assignment.week_number);
          }
        }
        // Fallback to submitted_at if submitted_week_numbers not available
        if (!isThisWeekSubmitted && assignment.submitted_at) {
          isThisWeekSubmitted = true;
        }
        // Show if not submitted for this week, or if submitted but marked as late
        return !isThisWeekSubmitted || getAssignmentStatus(assignment) === 'late';
      }
      return !assignment.submitted_at;
    });
    
    return unsubmitted
      .sort((a, b) => {
        const statusA = getAssignmentStatus(a);
        const statusB = getAssignmentStatus(b);
        
        // Prioritize current weekly assignments
        if (a.allow_weekly_submissions && a.week_number && !b.allow_weekly_submissions) return -1;
        if (b.allow_weekly_submissions && b.week_number && !a.allow_weekly_submissions) return 1;

        // Overdue first
        if (statusA === 'overdue' && statusB !== 'overdue') return -1;
        if (statusB === 'overdue' && statusA !== 'overdue') return 1;
        
        // Then sort by due date (soonest first)
        let dateA, dateB;
        try {
          if (a.display_due_date && DateTime.isDateTime(a.display_due_date)) {
            dateA = a.display_due_date;
          } else if (a.due_date) {
            dateA = utcToChicago(a.due_date);
          } else {
            dateA = DateTime.fromISO('2099-12-31');
          }
        } catch (e) {
          dateA = DateTime.fromISO('2099-12-31');
        }
        
        try {
          if (b.display_due_date && DateTime.isDateTime(b.display_due_date)) {
            dateB = b.display_due_date;
          } else if (b.due_date) {
            dateB = utcToChicago(b.due_date);
          } else {
            dateB = DateTime.fromISO('2099-12-31');
          }
        } catch (e) {
          dateB = DateTime.fromISO('2099-12-31');
        }
        
        // If both overdue or both pending, sort by date
        if (statusA === statusB) {
          return dateA.toMillis() - dateB.toMillis();
        }
        
        // Overdue should come before pending
        if (statusA === 'overdue') return -1;
        if (statusB === 'overdue') return 1;
        
        return dateA - dateB;
      })
      .slice(0, 5); // Limit to 5 items
  }, [assignments, currentWeeklyAssignments, getAssignmentStatus]);

  const quizSummary = useMemo(() => {
    // Filter only quizzes from active courses
    const activeQuizzes = quizzes.filter(q => q.course_is_active === 1 || q.course_is_active === true);
    const completed = activeQuizzes.filter(q => q.status === 'completed').length;
    const overdue = activeQuizzes.filter(q => {
      const status = getQuizStatus(q);
      return status === 'overdue';
    }).length;
    const open = activeQuizzes.filter(q => {
      const status = getQuizStatus(q);
      return status === 'pending' || status === 'in-progress';
    }).length;

    return { open, overdue, completed };
  }, [quizzes, getQuizStatus]);

  const recentQuizzes = useMemo(() => {
    // Filter only quizzes from active courses
    const activeQuizzes = quizzes.filter(q => q.course_is_active === 1 || q.course_is_active === true);
    return activeQuizzes
      .filter(quiz => quiz.status === 'not_started' || quiz.status === 'in_progress') // Only show not started or in progress quizzes
      .sort((a, b) => {
        const dateA = a.assignment_due_at || a.quiz_due_at ? new Date(a.assignment_due_at || a.quiz_due_at) : new Date('2099-12-31');
        const dateB = b.assignment_due_at || b.quiz_due_at ? new Date(b.assignment_due_at || b.quiz_due_at) : new Date('2099-12-31');
        return dateA - dateB; // Soonest due first
      });
  }, [quizzes]);

  const notesSummary = useMemo(() => {
    const total = notes.length;
    const pinned = notes.filter(n => n.is_pinned).length;
    const recent = notes.filter(n => {
      const noteDate = new Date(n.updated_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return noteDate > weekAgo;
    }).length;
    
    return { total, pinned, recent };
  }, [notes]);

  const recentNotes = useMemo(() => {
    return notes
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 3);
  }, [notes]);

  // Render instructor dashboard if user is an instructor
  if (user?.role === 'instructor') {
    return <InstructorDashboard />;
  }


  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const openAssignmentInline = (assignmentId, weekNumber = null) => {
    if (!assignmentId) {
      return;
    }
    let url = `/assignments?assignmentId=${encodeURIComponent(assignmentId)}`;
    if (weekNumber) {
      url += `&weekNumber=${encodeURIComponent(weekNumber)}`;
    }
    navigate(url);
  };

  const handleAssignmentClick = (assignmentId, weekNumber = null) => {
    openAssignmentInline(assignmentId, weekNumber);
  };

  const handleQuizClick = (assignmentId) => {
    navigate(`/student/assignments/${assignmentId}/warning`);
  };

  const getTotalPendingTasks = () => {
    let pendingCount = 0;

    // Use allActiveAssignments instead of assignments to get ALL active assignments
    // Count pending non-weekly assignments
    const nonWeeklyAssignments = allActiveAssignments.filter(a => !a.allow_weekly_submissions && !a.submitted_at);
    pendingCount += nonWeeklyAssignments.length;

    // Count pending weekly assignments (by week)
    const weeklyAssignments = allActiveAssignments.filter(a => a.allow_weekly_submissions);
    for (const assignment of weeklyAssignments) {
      // Calculate total weeks based on start_date
      let totalWeeks = 0;
      if (assignment.start_date) {
        const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
        const currentWeek = getCurrentWeekNumber(assignment.start_date, null, breakPeriods);
        const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
        const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
          ? Math.floor(parsedMaxWeeks)
          : 1;
        totalWeeks = Math.min(currentWeek > 0 ? currentWeek : 0, maxWeeks);
      }

      const submittedWeeks = assignment.submitted_week_numbers || [];
      const pendingWeeks = totalWeeks - submittedWeeks.length;
      pendingCount += Math.max(0, pendingWeeks);
    }

    // Count pending quizzes (quizzes that haven't been started or are in progress)
    const pendingQuizzes = quizzes.filter(q => (q.course_is_active === 1 || q.course_is_active === true) && (q.status === 'not_started' || q.status === 'in_progress')).length;
    pendingCount += pendingQuizzes;

    return pendingCount;
  };

  const getTotalCompletedTasks = () => {
    let completedCount = 0;

    // Use allActiveAssignments instead of assignments to get ALL active assignments
    // Count completed non-weekly assignments
    const nonWeeklyAssignments = allActiveAssignments.filter(a => !a.allow_weekly_submissions && a.submitted_at);
    completedCount += nonWeeklyAssignments.length;

    // Count completed weekly assignments (by week)
    const weeklyAssignments = allActiveAssignments.filter(a => a.allow_weekly_submissions);
    for (const assignment of weeklyAssignments) {
      const submittedWeeks = assignment.submitted_week_numbers || [];
      completedCount += submittedWeeks.length;
    }

    // Count completed quizzes (only quizzes with status 'completed')
    const completedQuizzes = quizzes.filter(q => (q.course_is_active === 1 || q.course_is_active === true) && q.status === 'completed').length;
    completedCount += completedQuizzes;

    return completedCount;
  };

  // Render admin dashboard for admin users
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <h1 className={styles.headerTitle}>
          <span dir="rtl">السلام عليكم</span>{' '}
          {user?.role === 'instructor' && <span dir="rtl">شيخ </span>}
          <span dir="ltr">{user?.first_name}</span>
        </h1>
        <p className={styles.headerSubtitle}>Here's what's happening with your courses today.</p>
      </div>

      {/* Active Attendance Sessions - Only show for students */}
      {user?.role === 'student' && <ActiveAttendanceSessions />}

      {/* My Assignments and My Quizzes Summary Sections - Only show for students */}
      {user?.role === 'student' && (
        <>
        {/* Row 1: Hero Assignments + Quizzes */}
        <div className={styles.topRow}>
          {/* Hero: My Assignments */}
          <motion.div
            className={`${styles.summaryCard} ${styles.heroCard}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className={styles.summaryHeader}>
              <h3 className={styles.summaryTitle}>
                <FiFileText size={20} />
                My Assignments
              </h3>
              <Link
                to="/assignments"
                className={styles.viewAllButton}
              >
                View All
              </Link>
            </div>
            <div className={styles.summaryContent}>
              <div className={styles.summaryStats}>
                {assignmentSummary.dueSoon > 0 && (
                  <div className={`${styles.summaryStatPill} ${styles.dueSoonChip}`}>
                    <span className={styles.summaryStatValue}>{assignmentSummary.dueSoon}</span>
                    <span className={styles.summaryStatLabel}>Open</span>
                  </div>
                )}
                <div className={`${styles.summaryStatPill} ${styles.completedChip}`}>
                  <span className={styles.summaryStatValue}>{assignmentSummary.completed}</span>
                  <span className={styles.summaryStatLabel}>Completed</span>
                </div>
                {assignmentSummary.overdue > 0 && (
                  <div className={`${styles.summaryStatPill} ${styles.overdueChip}`}>
                    <span className={styles.summaryStatValue}>{assignmentSummary.overdue}</span>
                    <span className={styles.summaryStatLabel}>Overdue</span>
                  </div>
                )}
              </div>
              <div className={styles.summaryItems}>
                {recentAssignments.length > 0 ? (
                  recentAssignments.map((assignment, index) => {
                    const status = getAssignmentStatus(assignment);
                    const dueDateText = formatDueDate(assignment.due_date, assignment);
                    // For weekly assignments, include week_number in key to ensure uniqueness
                    const uniqueKey = assignment.allow_weekly_submissions && assignment.week_number 
                      ? `${assignment.id}-week-${assignment.week_number}` 
                      : assignment.id || `assignment-${index}`;
                    return (
                      <div 
                        key={uniqueKey}
                        className={styles.listRow}
                        onClick={() => handleAssignmentClick(assignment.id, assignment.week_number)}
                        role="button"
                        tabIndex={0}
                        aria-label={`${(assignment.allow_weekly_submissions && assignment.week_number ? `Week ${assignment.week_number}: ` : '') + assignment.title}, due ${dueDateText}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleAssignmentClick(assignment.id);
                          }
                        }}
                      >
                        <div className={getStatusIconClass(status)}>
                          {status === 'completed' ? <FiCheckCircle size={16} /> :
                           status === 'overdue' ? <FiAlertTriangle size={16} /> :
                           <FiClock size={16} />}
                        </div>
                        <div className={styles.rowMain}>
                          <div className={styles.summaryItemTitle}>{(assignment.allow_weekly_submissions && assignment.week_number ? `Week ${assignment.week_number}: ` : '') + assignment.title}</div>
                          <div className={styles.rowMeta}>
                            <FiCalendar size={12} />
                            {formatDueDate(assignment.due_date, assignment)}
                          </div>
                        </div>
                        <div className={styles.rowChevron}>
                          <FiChevronRight size={16} />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptySummary}>
                    <FiFileText />
                    <p>You're all caught up.</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Right: My Quizzes */}
          <motion.div
            className={styles.summaryCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className={styles.summaryHeader}>
              <h3 className={styles.summaryTitle}>
                <FiEdit3 size={20} />
                My Quizzes
              </h3>
              <Link
                to="/student/quizzes"
                className={styles.viewAllButton}
              >
                View All
              </Link>
            </div>
            <div className={styles.summaryContent}>
              <div className={styles.summaryStats}>
                <div className={styles.summaryStatPill}>
                  <span className={styles.summaryStatValue}>{quizSummary.open}</span>
                  <span className={styles.summaryStatLabel}>Open</span>
                </div>
                <div className={styles.summaryStatPill}>
                  <span className={styles.summaryStatValue}>{quizSummary.completed}</span>
                  <span className={styles.summaryStatLabel}>Completed</span>
                </div>
                {quizSummary.overdue > 0 && (
                  <div className={`${styles.summaryStatPill} ${styles.overdueChip}`}>
                    <span className={styles.summaryStatValue}>{quizSummary.overdue}</span>
                    <span className={styles.summaryStatLabel}>Overdue</span>
                  </div>
                )}
              </div>
              <div className={styles.summaryItems}>
                {recentQuizzes.length > 0 ? (
                  recentQuizzes.map((quiz, index) => {
                    const status = getQuizStatus(quiz);
                    const dueDateText = formatDueDate(quiz.assignment_due_at || quiz.quiz_due_at);
                    return (
                      <div 
                        key={quiz.id || `quiz-${index}`}
                        className={styles.listRow}
                        onClick={() => handleQuizClick(quiz.assignment_id)}
                        role="button"
                        tabIndex={0}
                        aria-label={`${quiz.title}, due ${dueDateText}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleQuizClick(quiz.assignment_id);
                          }
                        }}
                      >
                        <div className={getStatusIconClass(status)}>
                          {status === 'completed' ? <FiCheckCircle size={16} /> :
                           status === 'overdue' ? <FiAlertTriangle size={16} /> :
                           status === 'in-progress' ? <FiClock size={16} /> :
                           <FiEdit3 size={16} />}
                        </div>
                        <div className={styles.rowMain}>
                          <div className={styles.summaryItemTitle}>{quiz.title}</div>
                          <div className={styles.rowMeta}>
                            <FiCalendar size={12} />
                            {formatDueDate(quiz.assignment_due_at || quiz.quiz_due_at)}
                          </div>
                        </div>
                        <div className={styles.rowChevron}>
                          <FiChevronRight size={16} />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptySummary}>
                    <FiEdit3 />
                    <p>No quizzes yet</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Row 2: Courses + Notes (Courses first for mobile priority) */}
        <div className={styles.secondRow}>
          {/* My Courses */}
          <motion.div
            className={`${styles.summaryCard} ${styles.coursesCard}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <div className={styles.summaryHeader}>
              <h3 className={styles.summaryTitle}>
                <FiBookOpen size={20} />
                My Courses
              </h3>
              {enrolledCourses.some(course => {
                const isActive = course.is_active === true || course.is_active === 1;
                const hasNoEndDate = !course.end_date || course.end_date === null;
                const endDateInFuture = course.end_date ? new Date(course.end_date) >= new Date() : false;
                return !(isActive && (hasNoEndDate || endDateInFuture));
              }) && (
                <button
                  className={styles.togglePreviousButton}
                  onClick={() => setShowPreviousCourses(!showPreviousCourses)}
                  type="button"
                >
                  {showPreviousCourses ? 'Hide Previous Courses' : 'Show Previous Courses'}
                </button>
              )}
            </div>
            <div className={styles.summaryContent}>
              <div className={styles.coursesGrid}>
                {(() => {
                  const filteredCourses = enrolledCourses.filter(course => {
                    if (showPreviousCourses) return true;
                    const isActive = course.is_active === true || course.is_active === 1;
                    const hasNoEndDate = !course.end_date || course.end_date === null;
                    const endDateInFuture = course.end_date ? new Date(course.end_date) >= new Date() : false;
                    return isActive && (hasNoEndDate || endDateInFuture);
                  });
                  return filteredCourses.length > 0 ? (
                    filteredCourses.slice(0, 6).map((course, index) => (
                    <motion.div
                      key={course.id || `course-${index}`}
                      className={styles.courseTile}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      onClick={() => handleCourseClick(course.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCourseClick(course.id);
                        }
                      }}
                    >
                      <div className={styles.courseBadge}>
                        {(course.code || course.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className={styles.courseTileInfo}>
                        <h4 className={styles.courseTileName}>{course.name}</h4>
                        <p className={styles.courseTileCode}>{course.code || 'No code'}</p>
                      </div>
                    </motion.div>
                    ))
                  ) : (
                    <div className={styles.emptySummary}>
                      <FiBookOpen />
                      <p>No courses enrolled yet.</p>
                    </div>
                  );
                })()}
              </div>
              {(() => {
                const filteredCourses = enrolledCourses.filter(course => {
                  if (showPreviousCourses) return true;
                  const isActive = course.is_active === true || course.is_active === 1;
                  const hasNoEndDate = !course.end_date || course.end_date === null;
                  const endDateInFuture = course.end_date ? new Date(course.end_date) >= new Date() : false;
                  return isActive && (hasNoEndDate || endDateInFuture);
                });
                return filteredCourses.length > 6 ? (
                  <div className={styles.moduleFooter}>
                    <Link
                      to="/courses"
                      className={styles.footerAction}
                    >
                      View all courses
                    </Link>
                  </div>
                ) : null;
              })()}
            </div>
          </motion.div>

          {/* My Notes */}
          <motion.div
            className={styles.summaryCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className={styles.summaryHeader}>
              <h3 className={styles.summaryTitle}>
                <FiBook size={20} />
                My Notes
              </h3>
              <Link
                to="/student/notes"
                className={styles.viewAllButton}
              >
                View All
              </Link>
            </div>
            <div className={styles.summaryContent}>
              <div className={styles.summaryStats}>
                <div className={styles.summaryStatPill}>
                  <span className={styles.summaryStatValue}>{notesSummary.total}</span>
                  <span className={styles.summaryStatLabel}>Total</span>
                </div>
                <div className={styles.summaryStatPill}>
                  <span className={styles.summaryStatValue}>{notesSummary.pinned}</span>
                  <span className={styles.summaryStatLabel}>Pinned</span>
                </div>
                <div className={styles.summaryStatPill}>
                  <span className={styles.summaryStatValue}>{notesSummary.recent}</span>
                  <span className={styles.summaryStatLabel}>Recent</span>
                </div>
              </div>
              <div className={styles.summaryItems}>
                {recentNotes.length > 0 ? (
                  recentNotes.map((note, index) => {
                    const updatedDate = new Date(note.updated_at).toLocaleDateString();
                    return (
                      <div 
                        key={note.id || `note-${index}`} 
                        className={styles.listRow}
                        onClick={() => navigate('/student/notes')}
                        role="button"
                        tabIndex={0}
                        aria-label={`${note.title}, updated ${updatedDate}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate('/student/notes');
                          }
                        }}
                      >
                      <div className={getStatusIconClass('completed')}>
                        <FiBook size={16} />
                      </div>
                      <div className={styles.rowMain}>
                        <div className={styles.summaryItemTitle}>{note.title}</div>
                        <div className={styles.rowMeta}>
                          <FiCalendar size={12} />
                          Updated: {new Date(note.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className={styles.rowChevron}>
                        <FiChevronRight size={16} />
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <div className={styles.emptySummary}>
                    <FiBook />
                    <p>No notes yet</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
        </>
      )}

      {/* Stats Strip - Unified Module - Only show for students */}
      {user?.role === 'student' && (
        <motion.div
          className={`${styles.summaryCard} ${styles.statsStrip}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <div className={styles.statsStripContent}>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('primary')}>
                <FiBookOpen size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{Number(stats.totalCourses) || 0}</div>
                <div className={styles.statLabel}>Active Courses</div>
              </div>
            </div>
            <div className={styles.statsDivider}></div>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('warning')}>
                <FiClock size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{getTotalPendingTasks()}</div>
                <div className={styles.statLabel}>Pending Tasks</div>
              </div>
            </div>
            <div className={styles.statsDivider}></div>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('success')}>
                <FiTrendingUp size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{getTotalCompletedTasks()}</div>
                <div className={styles.statLabel}>Completed Tasks</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recent Activity - Only show for students */}
      {user?.role === 'student' && (
        <motion.div
          className={`${styles.summaryCard} ${styles.activityCard}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <div className={styles.summaryHeader}>
            <h3 className={styles.summaryTitle}>
              <FiClock size={20} />
              Recent Activity
            </h3>
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryItems}>
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => {
                  // Map activity types to appropriate icons
                  const getActivityIcon = (type) => {
                    switch (type) {
                      case 'assignment':
                        return <FiFileText size={16} />;
                      case 'course':
                        return <FiBookOpen size={16} />;
                      case 'grade':
                        return <FiTrendingUp size={16} />;
                      default:
                        return <FiClock size={16} />;
                    }
                  };
                  
                  // Determine navigation target for activity
                  const handleActivityClick = () => {
                    if (activity.type === 'course' && activity.course_id) {
                      navigate(`/courses/${activity.course_id}`);
                    } else if (activity.type === 'assignment' && activity.assignment_id) {
                      openAssignmentInline(activity.assignment_id);
                    } else if (activity.type === 'grade' && activity.course_id) {
                      navigate(`/courses/${activity.course_id}`);
                    } else {
                      navigate('/dashboard');
                    }
                  };
                  
                  const hasNavigation = (activity.type === 'course' && activity.course_id) ||
                                       (activity.type === 'assignment' && activity.assignment_id) ||
                                       (activity.type === 'grade' && activity.course_id);
                  
                  return (
                    <div 
                      key={activity.id || `activity-${index}`} 
                      className={hasNavigation ? styles.listRow : styles.summaryItem}
                      onClick={hasNavigation ? handleActivityClick : undefined}
                      role={hasNavigation ? "button" : undefined}
                      tabIndex={hasNavigation ? 0 : undefined}
                      aria-label={hasNavigation ? `${activity.title}, ${activity.time}` : undefined}
                      onKeyDown={hasNavigation ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleActivityClick();
                        }
                      } : undefined}
                    >
                      <div className={styles.activityIcon}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className={styles.rowMain}>
                        <div className={styles.summaryItemTitle}>{activity.title}</div>
                        <div className={styles.rowMeta}>{activity.time}</div>
                      </div>
                      {hasNavigation && (
                        <div className={styles.rowChevron}>
                          <FiChevronRight size={16} />
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className={styles.emptySummary}>
                  <FiClock />
                  <p>No recent activity.</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Back to Top Button removed - using GlobalScrollToTop from App.js */}
    </div>
  );
}

export default Dashboard;
